import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import { Badge } from '@/components/ui/badge';
import {
    LayoutDashboard, BookOpen, CheckSquare, Users2, FileEdit, IndianRupee,
    CalendarRange, Sparkles, Bell, LogOut, Menu, X, Briefcase,
    ClipboardList, Trophy, ClipboardCheck, UserCog, Building2, Plug, Activity,
    CreditCard, Heart, Wallet, Settings as SettingsIcon, ChevronDown,
    Mail as MailIcon, FileSignature, Layers, Archive,
} from 'lucide-react';
import { api } from '@/lib/api';
import NotificationPanel from '@/components/NotificationPanel';
import {
    installAudioUnlock, notifyMaybePlay, updateTabTitle,
    resetNotificationSound, hasPendingPlay, onPendingPlayChange, flushPendingPlay,
} from '@/lib/notificationSound';

/* The nav items the brief wants in the centre pill. Everything else goes into
   the "More ▾" dropdown so the pill doesn't overflow on 1440px. */
const PRIMARY_INTERNAL = [
    { to: '/',               label: 'Dashboard',     icon: LayoutDashboard, exact: true },
    { to: '/reports',        label: 'Daily Reports', icon: ClipboardList },
    { to: '/tasks',          label: 'My Tasks',      icon: CheckSquare },
    { to: '/projects',       label: 'Projects',      icon: Briefcase },
    { to: '/content',        label: 'Content Hub',   icon: CalendarRange },
    { to: '/team-performance', label: 'Team',        icon: Trophy,      perm: 'team_performance' },
    { to: '/clients',        label: 'Clients',       icon: Building2,   perm: 'clients' },
    { to: '/leads',          label: 'Leads',         icon: Sparkles,    perm: 'leads' },
    { to: '/proposals',      label: 'Proposals',     icon: FileSignature, perm: 'proposals_view' },
    { to: '/assets',         label: 'Assets',        icon: Archive,     perm: 'assets_view' },
    { to: '/sops',           label: 'SOPs',          icon: BookOpen },
    { to: '/finance',        label: 'Finance',       icon: Wallet },
];

const SECONDARY_INTERNAL = [
    { to: '/subscriptions',  label: 'Subscriptions', icon: CreditCard, perm: 'finance_reviewer' },
    { to: '/books',          label: 'Books',         icon: FileEdit,   perm: 'finance_reviewer' },
    { to: '/change-requests',label: 'Change Requests', icon: FileEdit },
    { to: '/rate-cards',     label: 'Rate Cards',    icon: IndianRupee },
    { to: '/rate-catalog',   label: 'Rate Catalog',  icon: Layers,     perm: 'rates_manage' },
    { to: '/kudos',          label: 'Peer Kudos',    icon: Heart },
    { to: '/format-approvals', label: 'Format Approvals', icon: ClipboardCheck, roles: ['Owner', 'Admin', 'Operations'] },
    { to: '/users',          label: 'Team & Users',  icon: UserCog, roles: ['Owner', 'Admin'] },
    { to: '/integrations',   label: 'Integrations',  icon: Plug,    roles: ['Owner', 'Admin'] },
    { to: '/activity',       label: 'Activity',      icon: Activity },
];

const CLIENT_NAV = [
    { to: '/',               label: 'Overview',          icon: Briefcase, exact: true },
    { to: '/portal/proposals', label: 'Proposals',       icon: FileSignature },
    { to: '/reviews',        label: 'Approvals',         icon: ClipboardCheck },
    { to: '/reports',        label: 'Reports',           icon: ClipboardList },
    { to: '/change-requests',label: 'Change Requests',   icon: FileEdit },
];

/* Stable avatar colour derived from the user id. Keeps the same person in the
   same colour across sessions. Palette from the design tokens. */
function avatarPalette(seed = '') {
    const palette = [
        { bg: 'rgb(var(--blue))',      fg: '#ffffff' },
        { bg: 'rgb(var(--green))',     fg: '#ffffff' },
        { bg: 'rgb(var(--mint))',      fg: 'rgb(var(--ink))' },
        { bg: 'rgb(var(--ink))',       fg: '#ffffff' },
        { bg: 'rgb(var(--blue-tint))', fg: 'rgb(var(--blue-ink))' },
        { bg: 'rgb(var(--green-tint))',fg: 'rgb(var(--green-ink))' },
        { bg: 'rgb(var(--orange-tint))',fg: 'rgb(var(--rust))' },
    ];
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
}

export function Avatar({ name = '', seed, size = 40 }) {
    const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
    const { bg, fg } = avatarPalette(seed || name);
    return (
        <div
            className="inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0"
            style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.38 }}
        >
            {initials}
        </div>
    );
}

export default function AppLayout() {
    const { user, logout, isClient, hasPerm, isOwner } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [messagesUnread, setMessagesUnread] = useState(0);
    const [audioLocked, setAudioLocked] = useState(() => hasPendingPlay());
    const moreRef = useRef(null);

    const canSee = (n) => {
        if (n.roles && !n.roles.includes(user?.role)) return false;
        if (n.perm && !hasPerm(n.perm)) return false;
        return true;
    };

    const primaryNav   = isClient ? CLIENT_NAV : PRIMARY_INTERNAL.filter(canSee);
    const secondaryNav = isClient ? []         : SECONDARY_INTERNAL.filter(canSee);

    const loadUnread = async () => {
        try {
            const res = await api.get('/notifications');
            const rows = Array.isArray(res.data) ? res.data : [];
            const unread = rows.filter((n) => !n.read);
            setUnreadCount(unread.length);
            // Tab title stays in sync for everyone (staff + client portal).
            updateTabTitle(unread.length);
            // Pass the full fetched list — the sound layer uses an id-set
            // diff so it's immune to clock skew and detects any new id.
            notifyMaybePlay({ notifications: rows, user });
        } catch (_) {}
    };

    const loadMessagesUnread = async () => {
        if (isClient) return;
        try {
            const res = await api.get('/messages/unread-count');
            setMessagesUnread(res.data?.count || 0);
        } catch (_) {}
    };

    useEffect(() => {
        if (user) {
            // Install the global audio-unlock listeners (first click/key gesture
            // enables playback per browser autoplay rules). Idempotent.
            installAudioUnlock();
            loadUnread();
            loadMessagesUnread();
            // Short interval so new notifications surface quickly (≤10 s).
            // AppLayout is the single polling owner — nothing else polls.
            const t = setInterval(() => { loadUnread(); loadMessagesUnread(); }, 10000);
            // Refresh immediately when the tab becomes visible again, so a
            // user returning from another tab gets near-instant feedback
            // without waiting for the next tick.
            const onVisibility = () => {
                if (document.visibilityState === 'visible') {
                    loadUnread();
                    loadMessagesUnread();
                }
            };
            document.addEventListener('visibilitychange', onVisibility);
            window.addEventListener('focus', onVisibility);
            return () => {
                clearInterval(t);
                document.removeEventListener('visibilitychange', onVisibility);
                window.removeEventListener('focus', onVisibility);
            };
        }
    }, [user]);

    // Click-outside to close "More ▾"
    useEffect(() => {
        const onDoc = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    // Keep the top-of-app "Tap to enable sound" banner visible whenever a
    // notification has arrived but audio is still locked. Visible OUTSIDE
    // the bell panel so the user has a cue before clicking the bell.
    useEffect(() => {
        setAudioLocked(hasPendingPlay());
        return onPendingPlayChange(setAudioLocked);
    }, []);

    const handleLogout = () => {
        resetNotificationSound();
        logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-page">
            {/* Outer margin + the big rounded Soft Glass shell */}
            <div className="mx-auto max-w-[1440px] px-3 md:px-6 py-3 md:py-6">
                <div className="shine-shell overflow-hidden">
                    {/* Top bar */}
                    <header className="sticky top-0 z-40">
                        <div className="px-4 md:px-8 pt-4 md:pt-6 pb-3 grid grid-cols-[auto_1fr_auto] items-center gap-3">
                            {/* Left: logo capsule + mobile toggle */}
                            <div className="flex items-center gap-3 min-w-0">
                                <button
                                    data-testid="mobile-menu-toggle"
                                    className="lg:hidden icon-btn"
                                    onClick={() => setMobileOpen((s) => !s)}
                                    aria-label="Toggle menu"
                                >
                                    {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                                </button>
                                <Logo suffix={isClient ? 'Client Portal' : 'Ops Hub'} />
                            </div>

                            {/* Centre: pill nav (desktop only) */}
                            <nav className="hidden lg:flex items-center gap-0.5 justify-center flex-wrap min-w-0">
                                {primaryNav.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.exact}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        className={({ isActive }) => `shine-nav-link ${isActive ? 'active' : ''}`}
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                                {secondaryNav.length > 0 && (
                                    <div ref={moreRef} className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setMoreOpen((s) => !s)}
                                            data-testid="nav-more"
                                            className={`shine-nav-link inline-flex items-center gap-1 ${moreOpen ? 'active' : ''}`}
                                        >
                                            More <ChevronDown className="h-3.5 w-3.5" />
                                        </button>
                                        {moreOpen && (
                                            <div
                                                className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-56 bg-white rounded-tile p-2 z-50"
                                                style={{ boxShadow: '0 24px 60px rgba(17,20,24,.10)', border: '1px solid rgba(17,20,24,.06)' }}
                                            >
                                                {secondaryNav.map((item) => (
                                                    <NavLink
                                                        key={item.to}
                                                        to={item.to}
                                                        onClick={() => setMoreOpen(false)}
                                                        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                                        className={({ isActive }) =>
                                                            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                                                                isActive ? 'bg-ink text-white font-medium' : 'text-ink-2 hover:bg-page/70 hover:text-ink'
                                                            }`
                                                        }
                                                    >
                                                        <item.icon className="h-4 w-4" />
                                                        {item.label}
                                                    </NavLink>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </nav>

                            {/* Right: envelope (messages) + bell + settings (owner/admin) + avatar */}
                            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                                {!isClient && (
                                    <NavLink
                                        to="/messages"
                                        data-testid="messages-btn"
                                        className={({ isActive }) =>
                                            `relative icon-btn ${isActive ? 'bg-ink text-white' : ''}`
                                        }
                                        aria-label="Messages"
                                    >
                                        <MailIcon className="h-4.5 w-4.5" />
                                        {messagesUnread > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                                                style={{ background: 'rgb(var(--mint))', color: 'rgb(var(--ink))' }}>
                                                {messagesUnread}
                                            </span>
                                        )}
                                    </NavLink>
                                )}
                                <button
                                    data-testid="notif-toggle"
                                    onClick={() => setNotifOpen((s) => !s)}
                                    className="relative icon-btn"
                                    aria-label="Notifications"
                                >
                                    <Bell className="h-4.5 w-4.5" />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                                            style={{ background: 'rgb(var(--mint))', color: 'rgb(var(--ink))' }}>
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                                {!isClient && isOwner && (
                                    <NavLink
                                        to="/users"
                                        data-testid="header-settings-btn"
                                        className={({ isActive }) =>
                                            `icon-btn ${isActive ? 'bg-ink text-white' : ''}`
                                        }
                                        aria-label="Settings"
                                    >
                                        <SettingsIcon className="h-4.5 w-4.5" />
                                    </NavLink>
                                )}
                                <div className="hidden md:flex items-center gap-2.5 pl-1">
                                    <Avatar name={user?.name} seed={user?.id} size={40} />
                                    <div className="hidden lg:flex flex-col leading-tight">
                                        <span className="text-[14px] font-semibold text-ink" data-testid="current-user-name">{user?.name}</span>
                                        <span className="text-[12px] text-ink-3">{user?.role}</span>
                                    </div>
                                </div>
                                <button
                                    data-testid="logout-btn"
                                    onClick={handleLogout}
                                    className="icon-btn"
                                    title="Sign out"
                                    aria-label="Sign out"
                                >
                                    <LogOut className="h-4.5 w-4.5" />
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Mobile drawer */}
                    {mobileOpen && (
                        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setMobileOpen(false)}>
                            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                            <aside
                                className="absolute left-0 top-0 bottom-0 w-80 max-w-[88vw] bg-white p-4 space-y-1 overflow-y-auto"
                                style={{ boxShadow: '0 24px 60px rgba(17,20,24,.14)' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <Logo suffix={isClient ? 'Client Portal' : 'Ops Hub'} />
                                    <button className="icon-btn" onClick={() => setMobileOpen(false)} aria-label="Close">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                {[...primaryNav, ...secondaryNav].map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.exact}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid={`mnav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition ${
                                                isActive
                                                    ? 'bg-ink/5 text-ink font-semibold border border-ink/20'
                                                    : 'text-ink-2 hover:bg-page hover:text-ink'
                                            }`
                                        }
                                    >
                                        <item.icon className="h-4 w-4" />
                                        {item.label}
                                    </NavLink>
                                ))}
                                <div className="pt-4 mt-4 border-t" style={{ borderColor: 'rgba(17,20,24,.08)' }}>
                                    <div className="flex items-center gap-3 px-2">
                                        <Avatar name={user?.name} seed={user?.id} size={40} />
                                        <div className="leading-tight">
                                            <div className="text-sm font-semibold text-ink">{user?.name}</div>
                                            <div className="text-xs text-ink-3">{user?.role}</div>
                                        </div>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    )}

                    {notifOpen && (
                        <NotificationPanel onClose={() => { setNotifOpen(false); loadUnread(); }} />
                    )}

                    {/* Audio-locked banner — visible OUTSIDE the bell panel so
                        the user has a clear cue. Only renders for staff when
                        a notification is pending and audio is not yet
                        unlocked. Clicking the banner IS the user gesture
                        that unlocks audio and plays the pending sound. */}
                    {!isClient && audioLocked && (
                        <button
                            type="button"
                            data-testid="audio-unlock-banner"
                            onClick={() => { flushPendingPlay(); setAudioLocked(false); }}
                            className="mx-4 md:mx-8 my-2 flex items-center justify-center gap-2 rounded-tile w-[calc(100%-2rem)] md:w-[calc(100%-4rem)] px-4 py-2 text-sm font-medium transition"
                            style={{
                                background: 'linear-gradient(90deg, rgba(251,191,36,.18), rgba(251,191,36,.10))',
                                border: '1px solid rgba(251,191,36,.45)',
                                color: '#92400e',
                            }}
                        >
                            <Bell className="h-4 w-4" />
                            <span>Tap here to enable sound for new notifications</span>
                        </button>
                    )}

                    {/* Main content */}
                    <main className="px-4 md:px-8 pb-6 md:pb-10 pt-2">
                        <Outlet />
                    </main>
                </div>

                {/* Footer line outside the shell for breathing room */}
                <footer className="py-4 text-center text-xs text-ink-3">
                    © {new Date().getFullYear()}-{String((new Date().getFullYear() + 1) % 100).padStart(2, "0")} SHINE AD AGENCY · Vizag · Hyderabad
                </footer>
            </div>
        </div>
    );
}
